import React, { useState, useEffect, useCallback } from 'react';
import { Can } from '../components/Can';
import {
  Brain, Calendar, TrendingUp, TrendingDown, Target, Shield, AlertTriangle,
  ChevronLeft, ChevronRight, RefreshCw, FileText, Tag, Clock, Award,
  BarChart3, Zap, Eye, ChevronDown, ChevronUp, Settings, Save, Check,
  Loader2, Bot, ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

const API_BASE = '/api/scorecard';

function getHeaders() {
  return {
    'Authorization': 'Bearer ' + localStorage.getItem('token'),
    'Content-Type': 'application/json'
  };
}

// Grade color mapping
function getGradeColor(grade) {
  if (!grade || grade === 'N/A') return 'text-text-secondary';
  const letter = grade.charAt(0);
  if (letter === 'A') return 'text-green-400';
  if (letter === 'B') return 'text-blue-400';
  if (letter === 'C') return 'text-yellow-400';
  if (letter === 'D') return 'text-orange-400';
  return 'text-red-400';
}

function getGradeBg(grade) {
  if (!grade || grade === 'N/A') return 'bg-white/5';
  const letter = grade.charAt(0);
  if (letter === 'A') return 'bg-green-500/15 border-green-500/30';
  if (letter === 'B') return 'bg-blue-500/15 border-blue-500/30';
  if (letter === 'C') return 'bg-yellow-500/15 border-yellow-500/30';
  if (letter === 'D') return 'bg-orange-500/15 border-orange-500/30';
  return 'bg-red-500/15 border-red-500/30';
}

function PnlDisplay({ value, size = 'md' }) {
  const isPositive = value > 0;
  const isZero = value === 0;
  const sizeClasses = size === 'lg' ? 'text-2xl font-bold' : size === 'sm' ? 'text-sm' : 'text-base font-semibold';
  const color = isZero ? 'text-text-secondary' : isPositive ? 'text-green-400' : 'text-red-400';
  const Icon = isPositive ? ArrowUpRight : isZero ? Minus : ArrowDownRight;

  return (
    <span className={`${sizeClasses} ${color} inline-flex items-center gap-1`}>
      <Icon className={size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'} />
      ${Math.abs(value).toFixed(2)}
    </span>
  );
}

function RiskGauge({ score }) {
  const percentage = (score / 10) * 100;
  const color = score >= 8 ? '#22c55e' : score >= 6 ? '#3b82f6' : score >= 4 ? '#eab308' : '#ef4444';

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-sm font-mono font-bold" style={{ color }}>{score}/10</span>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color = 'text-accent-blue' }) {
  return (
    <div className="bg-bg-secondary/50 rounded-xl p-4 border border-border-color/30">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-text-secondary uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-bold text-text-primary">{value}</div>
      {sub && <div className="text-xs text-text-secondary mt-1">{sub}</div>}
    </div>
  );
}

// AI Status config panel
function AIConfigPanel({ aiStatus, onSave }) {
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/ai-config`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ apiKey })
      });
      const data = await res.json();
      if (data.success) {
        setApiKey('');
        onSave && onSave();
      }
    } catch (err) {
      console.error('Failed to save API key:', err);
    }
    setSaving(false);
  };

  return (
    <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 overflow-hidden">
      <button
        onClick={() => setShowConfig(!showConfig)}
        className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${aiStatus?.configured ? 'bg-green-500/15' : 'bg-yellow-500/15'}`}>
            <Brain className={`w-5 h-5 ${aiStatus?.configured ? 'text-green-400' : 'text-yellow-400'}`} />
          </div>
          <div className="text-left">
            <div className="font-semibold text-text-primary">
              {aiStatus?.agentName || 'Analyst Alpha'} — {aiStatus?.agentRole || 'AI Analyst'}
            </div>
            <div className="text-xs text-text-secondary">
              {aiStatus?.configured
                ? `Connected • Key: ${aiStatus.keyPreview}`
                : 'Not configured — using rule-based analysis'
              }
            </div>
          </div>
        </div>
        <Settings className="w-4 h-4 text-text-secondary" />
      </button>

      {showConfig && (
        <div className="px-5 pb-5 border-t border-border-color/20">
          <p className="text-sm text-text-secondary mt-4 mb-3">
            Enter your Anthropic API key to enable Claude-powered daily analysis.
            Without it, the system uses built-in rule-based scoring.
          </p>
          <div className="flex gap-2">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="sk-ant-api03-..."
              className="flex-1 bg-bg-primary border border-border-color/40 rounded-lg px-3 py-2 text-sm text-text-primary placeholder:text-text-secondary/50 focus:outline-none focus:border-accent-blue/50"
            />
            <button
              onClick={handleSave}
              disabled={!apiKey || saving}
              className="px-4 py-2 bg-accent-blue/20 text-accent-blue rounded-lg text-sm font-medium hover:bg-accent-blue/30 disabled:opacity-40 transition-colors flex items-center gap-2"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Period summary bar
function PeriodSummary({ summary }) {
  if (!summary || summary.tradingDays === 0) return null;

  return (
    <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-4 h-4 text-accent-purple" />
        <span className="text-sm font-semibold text-text-primary">
          {summary.period} Performance
        </span>
        <span className="text-xs text-text-secondary ml-auto">
          {summary.tradingDays} trading days • {summary.totalTrades} trades
        </span>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div>
          <div className="text-xs text-text-secondary mb-1">Total P&L</div>
          <PnlDisplay value={summary.totalPnl} />
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1">Avg Daily</div>
          <PnlDisplay value={summary.avgDailyPnl} />
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1">Win Rate</div>
          <div className="text-base font-semibold text-text-primary">{summary.avgWinRate}%</div>
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1">Streak</div>
          <div className={`text-base font-semibold ${summary.streaks?.current?.includes('W') ? 'text-green-400' : 'text-red-400'}`}>
            {summary.streaks?.current || '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-text-secondary mb-1">Best / Worst</div>
          <div className="text-xs">
            <span className="text-green-400">${summary.bestDay?.pnl?.toFixed(0) || '0'}</span>
            <span className="text-text-secondary mx-1">/</span>
            <span className="text-red-400">${summary.worstDay?.pnl?.toFixed(0) || '0'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Single scorecard view
function ScorecardDetail({ scorecard, onNotesUpdate }) {
  const [notes, setNotes] = useState(scorecard?.userNotes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [expanded, setExpanded] = useState({ trades: false, raw: false });

  const a = scorecard?.analysis;
  if (!a) return null;

  const saveNotes = async () => {
    setSaving(true);
    try {
      await fetch(`${API_BASE}/notes/${scorecard.date}`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ notes })
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onNotesUpdate && onNotesUpdate();
    } catch (err) {
      console.error('Failed to save notes:', err);
    }
    setSaving(false);
  };

  return (
    <div className="space-y-5">
      {/* Header: Grade + P&L */}
      <div className="flex flex-col md:flex-row gap-5">
        {/* Grade Card */}
        <div className={`flex-shrink-0 rounded-2xl border p-6 flex flex-col items-center justify-center min-w-[160px] ${getGradeBg(a.grade)}`}>
          <div className={`text-5xl font-black tracking-tight ${getGradeColor(a.grade)}`}>
            {a.grade}
          </div>
          <div className="text-xs text-text-secondary mt-2 text-center">{a.gradeSummary}</div>
          {a.analysisSource && (
            <div className="mt-3 flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${a.analysisSource === 'claude-ai' ? 'bg-green-400' : 'bg-yellow-400'}`} />
              <span className="text-[10px] text-text-secondary uppercase tracking-wider">
                {a.analysisSource === 'claude-ai' ? 'AI Analysis' : 'Rule-Based'}
              </span>
            </div>
          )}
        </div>

        {/* P&L Stats Grid */}
        <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={TrendingUp} label="Net P&L" value={<PnlDisplay value={a.pnl?.net || 0} />} color="text-green-400" />
          <StatCard icon={Target} label="Win Rate" value={`${a.pnl?.winRate || 0}%`} sub={`${a.pnl?.winners || 0}W / ${a.pnl?.losers || 0}L`} color="text-accent-blue" />
          <StatCard icon={Award} label="Avg Win" value={`$${Math.abs(a.pnl?.avgWin || 0).toFixed(2)}`} sub={`Best: $${(a.pnl?.largestWin || 0).toFixed(2)}`} color="text-green-400" />
          <StatCard icon={AlertTriangle} label="Avg Loss" value={`$${Math.abs(a.pnl?.avgLoss || 0).toFixed(2)}`} sub={`Worst: $${Math.abs(a.pnl?.largestLoss || 0).toFixed(2)}`} color="text-red-400" />
        </div>
      </div>

      {/* Risk + Regime Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Shield className="w-4 h-4 text-accent-blue" />
            <span className="text-sm font-semibold text-text-primary">Risk Management</span>
          </div>
          <RiskGauge score={a.riskScore || 0} />
          <p className="text-sm text-text-secondary mt-3">{a.riskNotes}</p>
        </div>

        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-4 h-4 text-accent-yellow" />
            <span className="text-sm font-semibold text-text-primary">Regime Assessment</span>
          </div>
          <p className="text-sm text-text-secondary">{a.regimeAssessment}</p>
        </div>
      </div>

      {/* Best / Worst Trade */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {a.bestTrade && a.bestTrade.symbol !== 'N/A' && (
          <div className="bg-green-500/5 rounded-xl border border-green-500/20 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ArrowUpRight className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-green-400">Best Trade</span>
              </div>
              <span className="text-lg font-bold text-green-400">{a.bestTrade.symbol}</span>
            </div>
            <PnlDisplay value={a.bestTrade.pnl} size="sm" />
            <p className="text-sm text-text-secondary mt-2">{a.bestTrade.analysis}</p>
          </div>
        )}
        {a.worstTrade && a.worstTrade.symbol !== 'N/A' && (
          <div className="bg-red-500/5 rounded-xl border border-red-500/20 p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <ArrowDownRight className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-400">Worst Trade</span>
              </div>
              <span className="text-lg font-bold text-red-400">{a.worstTrade.symbol}</span>
            </div>
            <PnlDisplay value={a.worstTrade.pnl} size="sm" />
            <p className="text-sm text-text-secondary mt-2">{a.worstTrade.analysis}</p>
          </div>
        )}
      </div>

      {/* Pattern Flags */}
      {a.patternFlags && a.patternFlags.length > 0 && (
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-4 h-4 text-accent-orange" />
            <span className="text-sm font-semibold text-text-primary">Pattern Flags</span>
          </div>
          <div className="space-y-2">
            {a.patternFlags.map((flag, i) => (
              <div key={i} className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-3.5 h-3.5 text-accent-orange mt-0.5 flex-shrink-0" />
                <span className="text-text-secondary">{flag}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Narrative */}
      {a.narrative && (
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="w-4 h-4 text-accent-purple" />
            <span className="text-sm font-semibold text-text-primary">
              {a.analysisSource === 'claude-ai' ? 'Analyst Alpha\'s Take' : 'Daily Summary'}
            </span>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed whitespace-pre-line">{a.narrative}</p>
        </div>
      )}

      {/* Tomorrow + Action Items */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {a.tomorrowOutlook && (
          <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-accent-blue" />
              <span className="text-sm font-semibold text-text-primary">Tomorrow's Outlook</span>
            </div>
            <p className="text-sm text-text-secondary">{a.tomorrowOutlook}</p>
          </div>
        )}
        {a.actionItems && a.actionItems.length > 0 && (
          <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Check className="w-4 h-4 text-green-400" />
              <span className="text-sm font-semibold text-text-primary">Action Items</span>
            </div>
            <div className="space-y-2">
              {a.actionItems.map((item, i) => (
                <div key={i} className="flex items-start gap-2 text-sm">
                  <div className="w-5 h-5 rounded-md bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-[10px] text-text-secondary font-mono">{i + 1}</span>
                  </div>
                  <span className="text-text-secondary">{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User Notes */}
      <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-accent-green" />
            <span className="text-sm font-semibold text-text-primary">My Notes</span>
          </div>
          <button
            onClick={saveNotes}
            disabled={saving || notes === (scorecard?.userNotes || '')}
            className="px-3 py-1.5 bg-accent-green/15 text-accent-green rounded-lg text-xs font-medium hover:bg-accent-green/25 disabled:opacity-30 transition-colors flex items-center gap-1.5"
          >
            {saved ? <Check className="w-3.5 h-3.5" /> : saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saved ? 'Saved' : 'Save Notes'}
          </button>
        </div>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Add your observations, thoughts, or things to remember about today's trading session..."
          rows={4}
          className="w-full bg-bg-primary border border-border-color/30 rounded-lg px-4 py-3 text-sm text-text-primary placeholder:text-text-secondary/40 resize-y focus:outline-none focus:border-accent-green/40 transition-colors"
        />
      </div>

      {/* Trade Details Expandable */}
      {scorecard?.rawData?.trades?.length > 0 && (
        <div className="bg-bg-secondary/50 rounded-xl border border-border-color/30 overflow-hidden">
          <button
            onClick={() => setExpanded(p => ({ ...p, trades: !p.trades }))}
            className="w-full px-5 py-4 flex items-center justify-between hover:bg-white/5 transition-colors"
          >
            <span className="text-sm font-semibold text-text-primary">
              Trade Details ({scorecard.rawData.trades.length} trades)
            </span>
            {expanded.trades ? <ChevronUp className="w-4 h-4 text-text-secondary" /> : <ChevronDown className="w-4 h-4 text-text-secondary" />}
          </button>
          {expanded.trades && (
            <div className="px-5 pb-5 border-t border-border-color/20">
              <div className="overflow-x-auto mt-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-text-secondary text-xs uppercase tracking-wider">
                      <th className="text-left py-2 pr-4">Symbol</th>
                      <th className="text-left py-2 pr-4">Side</th>
                      <th className="text-right py-2 pr-4">Qty</th>
                      <th className="text-right py-2 pr-4">Entry</th>
                      <th className="text-right py-2 pr-4">Exit</th>
                      <th className="text-right py-2 pr-4">P&L</th>
                      <th className="text-left py-2">Strategy</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scorecard.rawData.trades.map((t, i) => (
                      <tr key={i} className="border-t border-border-color/10">
                        <td className="py-2 pr-4 font-medium text-text-primary">{t.symbol}</td>
                        <td className="py-2 pr-4">
                          <span className={t.side === 'buy' ? 'text-green-400' : 'text-red-400'}>
                            {(t.side || '').toUpperCase()}
                          </span>
                        </td>
                        <td className="py-2 pr-4 text-right text-text-secondary">{t.qty || t.quantity || '-'}</td>
                        <td className="py-2 pr-4 text-right text-text-secondary">${(t.entryPrice || t.price || 0).toFixed(2)}</td>
                        <td className="py-2 pr-4 text-right text-text-secondary">${(t.exitPrice || 0).toFixed(2)}</td>
                        <td className="py-2 pr-4 text-right">
                          {t.pnl !== undefined ? <PnlDisplay value={t.pnl} size="sm" /> : <span className="text-text-secondary">—</span>}
                        </td>
                        <td className="py-2 text-text-secondary text-xs">{t.strategy || t.exitReason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Scorecard list item (sidebar / timeline)
function ScorecardListItem({ scorecard, isSelected, onClick }) {
  const a = scorecard?.analysis;
  const dateObj = new Date(scorecard.date + 'T12:00:00');
  const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
  const dateDisplay = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const hasNotes = scorecard.userNotes && scorecard.userNotes.trim().length > 0;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-xl transition-all ${
        isSelected
          ? 'bg-accent-blue/15 border border-accent-blue/30'
          : 'hover:bg-white/5 border border-transparent'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`text-xl font-black w-10 text-center ${getGradeColor(a?.grade)}`}>
            {a?.grade || '—'}
          </div>
          <div>
            <div className="text-sm font-medium text-text-primary">{dayName}, {dateDisplay}</div>
            <div className="text-xs text-text-secondary">
              {a?.pnl?.totalTrades || 0} trades
              {hasNotes && <span className="ml-2 text-accent-green">• has notes</span>}
            </div>
          </div>
        </div>
        <div className="text-right">
          {a?.pnl?.net !== undefined && <PnlDisplay value={a.pnl.net} size="sm" />}
        </div>
      </div>
    </button>
  );
}

// Main Scorecard Page
export default function Scorecard() {
  const [scorecards, setScorecards] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [summary, setSummary] = useState(null);
  const [aiStatus, setAiStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summaryDays, setSummaryDays] = useState(7);

  const fetchScorecards = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/list?limit=30`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) {
        setScorecards(data.scorecards);
        if (data.scorecards.length > 0 && !selectedDate) {
          setSelectedDate(data.scorecards[0].date);
          setSelectedCard(data.scorecards[0]);
        }
      }
    } catch (err) {
      console.error('Failed to fetch scorecards:', err);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/summary?days=${summaryDays}`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) setSummary(data.summary);
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  }, [summaryDays]);

  const fetchAiStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/ai-status`, { headers: getHeaders() });
      const data = await res.json();
      if (data.success) setAiStatus(data);
    } catch (err) {
      console.error('Failed to fetch AI status:', err);
    }
  };

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchScorecards(), fetchSummary(), fetchAiStatus()]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    fetchSummary();
  }, [summaryDays]);

  useEffect(() => {
    if (selectedDate) {
      const card = scorecards.find(s => s.date === selectedDate);
      setSelectedCard(card || null);
    }
  }, [selectedDate, scorecards]);

  const generateToday = async () => {
    setGenerating(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const res = await fetch(`${API_BASE}/generate`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ date: today, force: true })
      });
      const data = await res.json();
      if (data.success) {
        await fetchScorecards();
        setSelectedDate(today);
      }
    } catch (err) {
      console.error('Failed to generate scorecard:', err);
    }
    setGenerating(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-primary flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent-blue mx-auto mb-3" />
          <div className="text-sm text-text-secondary">Loading scorecards...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-primary">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-accent-purple/15 flex items-center justify-center">
                <Brain className="w-5 h-5 text-accent-purple" />
              </div>
              Daily Scorecards
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              AI-powered daily trading performance analysis
            </p>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={summaryDays}
              onChange={(e) => setSummaryDays(parseInt(e.target.value))}
              className="bg-bg-secondary border border-border-color/30 rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none"
            >
              <option value={7}>7 Days</option>
              <option value={14}>14 Days</option>
              <option value={30}>30 Days</option>
            </select>
            <Can perm="action.scorecard.generate">
              <button
                onClick={generateToday}
                disabled={generating}
                className="px-4 py-2 bg-accent-purple/15 text-accent-purple rounded-lg text-sm font-medium hover:bg-accent-purple/25 disabled:opacity-40 transition-colors flex items-center gap-2"
              >
                {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Generate Today
              </button>
            </Can>
          </div>
        </div>

        {/* AI Config Panel */}
        <div className="mb-5">
          <AIConfigPanel aiStatus={aiStatus} onSave={fetchAiStatus} />
        </div>

        {/* Period Summary */}
        {summary && (
          <div className="mb-5">
            <PeriodSummary summary={summary} />
          </div>
        )}

        {/* Main Content: Sidebar + Detail */}
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Sidebar: Scorecard List */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="bg-bg-secondary/30 rounded-xl border border-border-color/20 p-3 space-y-1 lg:sticky lg:top-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {scorecards.length === 0 ? (
                <div className="py-8 text-center">
                  <Bot className="w-10 h-10 text-text-secondary/30 mx-auto mb-3" />
                  <p className="text-sm text-text-secondary">No scorecards yet</p>
                  <p className="text-xs text-text-secondary/60 mt-1">Generate your first one above</p>
                </div>
              ) : (
                scorecards.map(sc => (
                  <ScorecardListItem
                    key={sc.date}
                    scorecard={sc}
                    isSelected={sc.date === selectedDate}
                    onClick={() => setSelectedDate(sc.date)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Detail View */}
          <div className="flex-1 min-w-0">
            {selectedCard ? (
              <ScorecardDetail
                scorecard={selectedCard}
                onNotesUpdate={fetchScorecards}
              />
            ) : (
              <div className="bg-bg-secondary/30 rounded-xl border border-border-color/20 p-12 text-center">
                <Brain className="w-12 h-12 text-text-secondary/20 mx-auto mb-4" />
                <p className="text-text-secondary">Select a scorecard or generate today's report</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
